import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API } from '@/utils/constants';

/**
 * Quote Approval Page
 * Public page for customers to accept or reject quotes
 */
const QuoteApprovalPage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [responseSubmitted, setResponseSubmitted] = useState(false);
  const [responseStatus, setResponseStatus] = useState(null);

  useEffect(() => {
    fetchQuote();
  }, [token]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quote-approval/${token}`);
      setQuote(response.data);
      // Check if already responded
      if (response.data.approval_status === 'accepted' || response.data.approval_status === 'rejected') {
        setResponseSubmitted(true);
        setResponseStatus(response.data.approval_status);
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      toast.error('Invalid or expired quote link');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/quote-approval/${token}`, {
        action: 'accept'
      });
      toast.success(response.data.message);
      setResponseSubmitted(true);
      setResponseStatus('accepted');
    } catch (error) {
      console.error('Error accepting quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/quote-approval/${token}`, {
        action: 'reject',
        rejection_reason: rejectionReason
      });
      toast.success(response.data.message);
      setResponseSubmitted(true);
      setResponseStatus('rejected');
    } catch (error) {
      console.error('Error rejecting quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-signature-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="public-signature-page">
        <Card className="signature-card">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
          </CardHeader>
          <CardContent>
            <p>This quote approval link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="public-signature-page">
      <div className="signature-branding">
        {quote.business_info?.logo_url ? (
          <img src={quote.business_info.logo_url} alt={quote.business_info?.business_name || 'Business'} className="signature-logo" />
        ) : (
          <img src="/logo.jpg" alt="Business Logo" className="signature-logo" />
        )}
        <h1>{quote.business_info?.business_name || 'Quote Approval'}</h1>
      </div>

      <Card className="signature-card" style={{ maxWidth: '600px' }}>
        <CardHeader>
          <CardTitle>Quote for Job #{quote.job_number}</CardTitle>
          <CardDescription>
            {responseSubmitted 
              ? `You have ${responseStatus} this quote`
              : 'Please review and respond to the quote below'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="job-card-details">
            <div className="detail-row">
              <strong>Customer Name:</strong> {quote.customer_name}
            </div>
            <div className="detail-row">
              <strong>Device:</strong> {quote.device_info}
            </div>
            {quote.issue && (
              <div className="detail-row">
                <strong>Issue:</strong> {quote.issue}
              </div>
            )}
            
            <div style={{ 
              marginTop: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#f0f9ff', 
              borderRadius: '0.5rem',
              border: '1px solid #bfdbfe'
            }}>
              <h4 style={{ color: '#1e40af', marginBottom: '0.75rem', fontSize: '1rem' }}>
                Issue Summary & Proposed Solution
              </h4>
              <p style={{ whiteSpace: 'pre-wrap', color: '#374151', lineHeight: '1.6' }}>
                {quote.quote_text}
              </p>
            </div>

            {quote.quote_amount != null && quote.quote_amount > 0 && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                backgroundColor: '#fef3c7', 
                borderRadius: '0.5rem',
                border: '1px solid #fcd34d',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.25rem' }}>
                  Quoted Amount
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#b45309' }}>
                  ${quote.quote_amount.toFixed(2)}
                </div>
              </div>
            )}

            {/* Response Section */}
            {responseSubmitted ? (
              <div style={{ 
                marginTop: '1.5rem', 
                padding: '1.5rem', 
                backgroundColor: responseStatus === 'accepted' ? '#d1fae5' : '#fee2e2', 
                borderRadius: '0.5rem',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '3rem', 
                  marginBottom: '0.5rem' 
                }}>
                  {responseStatus === 'accepted' ? '✓' : '✗'}
                </div>
                <h3 style={{ 
                  color: responseStatus === 'accepted' ? '#059669' : '#dc2626',
                  marginBottom: '0.5rem'
                }}>
                  {responseStatus === 'accepted' 
                    ? (quote.confirmation_messages?.accepted?.title || 'Quote Accepted')
                    : (quote.confirmation_messages?.declined?.title || 'Quote Declined')
                  }
                </h3>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  {responseStatus === 'accepted' 
                    ? (quote.confirmation_messages?.accepted?.message || 'Thank you for accepting the quote. We will be in touch shortly to proceed with the work.')
                    : (quote.confirmation_messages?.declined?.message || 'You have declined this quote. A collection notice has been sent to your email. Please collect your item within 7 days.')
                  }
                </p>
              </div>
            ) : (
              <div style={{ marginTop: '1.5rem' }}>
                {!showRejectForm ? (
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    <Button
                      onClick={handleAccept}
                      disabled={submitting}
                      style={{ 
                        backgroundColor: '#059669', 
                        color: 'white',
                        padding: '0.75rem 2rem',
                        fontSize: '1rem'
                      }}
                      data-testid="accept-quote-button"
                    >
                      {submitting ? 'Submitting...' : '✓ Accept Quote'}
                    </Button>
                    <Button
                      onClick={() => setShowRejectForm(true)}
                      disabled={submitting}
                      variant="outline"
                      style={{ 
                        borderColor: '#dc2626', 
                        color: '#dc2626',
                        padding: '0.75rem 2rem',
                        fontSize: '1rem'
                      }}
                      data-testid="decline-quote-button"
                    >
                      ✗ Decline Quote
                    </Button>
                  </div>
                ) : (
                  <div style={{ 
                    padding: '1rem', 
                    backgroundColor: '#fef2f2', 
                    borderRadius: '0.5rem',
                    border: '1px solid #fecaca'
                  }}>
                    <h4 style={{ color: '#dc2626', marginBottom: '0.75rem' }}>
                      Decline Quote
                    </h4>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please let us know why you're declining (optional)"
                      rows={3}
                      style={{ marginBottom: '1rem' }}
                      data-testid="rejection-reason-input"
                    />
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <Button
                        onClick={handleReject}
                        disabled={submitting}
                        style={{ backgroundColor: '#dc2626', color: 'white' }}
                        data-testid="confirm-decline-button"
                      >
                        {submitting ? 'Submitting...' : 'Confirm Decline'}
                      </Button>
                      <Button
                        onClick={() => setShowRejectForm(false)}
                        disabled={submitting}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.75rem' }}>
                      Note: By declining, your job status will be changed to "Not Proceeding" and you will receive an email to collect your item within 7 days.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div style={{ 
              marginTop: '2rem', 
              paddingTop: '1rem', 
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}>
              <p>Questions? Contact us:</p>
              {quote.business_info?.phone && <p>{quote.business_info.phone}</p>}
              {quote.business_info?.email && <p>{quote.business_info.email}</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteApprovalPage;
